import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert } from 'app/shared/form-alert';
import { Icon } from 'assets/icons';
import { isEmailValid } from 'app/shared/utils/input';
import { generateUniqueId } from 'app/shared/utils/general';
import { useGuestService } from 'hooks/guest';
import { urls } from 'services/_url';
import styles from './password.module.css';

const PasswordRecovery = () => {
  const { recoverPassword } = useGuestService();
  const [alert, setAlert] = useState({});
  const [form_data, setFormData] = useState({ email: '' });
  const [is_loading, setIsLoading] = useState(false);
  const [is_request_disabled, setRequestDisabled] = useState(false);
  const [request_timeout, setRequestTimeout] = useState(null);
  const [errors, setErrors] = useState({
    email: ''
  });
  const history = useNavigate();

  const validateEmail = (value) => {
    let errorMessage = '';
    if (!value.trim()) {
      errorMessage = 'Email address is required.';
    } else if (!isEmailValid(value)) {
      errorMessage = 'Invalid email address.';
    }
    setErrors({ email: errorMessage });
  };

  const handleEmailChange = (value) => {
    setFormData({ email: value });
    validateEmail(value);
  };

  const submitForm = () => {
    if (disableSubmitButton) {
      validateEmail(form_data.email);
      return;
    }

    if (is_request_disabled) {
      setAlert({
        message: 'Please wait for few minutes before requesting again.',
        id: generateUniqueId()
      });
      return;
    }

    setIsLoading(true);
    recoverPassword({ data: form_data })
      .then((response) => {
        const { error, payload } = response;

        if (error) {
          setAlert({ message: error, id: generateUniqueId() });
          return;
        }

        setAlert({
          message: payload,
          type: 'success'
        });

        setRequestDisabled(true);
        const timeout = setTimeout(() => {
          setRequestDisabled(false);
        }, 60000);
        setRequestTimeout(timeout);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    return () => {
      clearTimeout(request_timeout);
    };
  }, []);

  const isFormDisabled = (errors, values) => {
    const hasErrors = Object.values(errors).some((error) => error !== '');
    const isFieldsIncomplete = Object.values(values).some((value) => !value.trim());
    return hasErrors || isFieldsIncomplete;
  };

  const disableSubmitButton = isFormDisabled(errors, { password: form_data.email });

  return (
    <div className={styles.pageWrapper}>
      <a href={urls.landing} className={styles.homeBtn}>
        <Icon name="home" width={16} height={16} />
        Home
      </a>

      <div className={styles.formInner}>
        <h1 className={styles.formHeading}>Forgot password?</h1>
        <p className={styles.formSubhead}>
          Enter your registered email address and we'll send you a link to reset your password.
        </p>

        {alert.message && (
          <div className={styles.alertWrap}>
            <Alert {...alert} />
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submitForm();
          }}
        >
          <div className={styles.fieldGroup}>
            <div>
              <label className={styles.fieldLabel} htmlFor="recovery-email">
                Email address
              </label>
              <input
                id="recovery-email"
                type="email"
                autoComplete="email"
                autoFocus
                placeholder="you@example.com"
                value={form_data.email}
                onChange={(e) => handleEmailChange(e.target.value)}
                className={`${styles.input}${errors.email ? ` ${styles.inputError}` : ''}`}
              />
              {errors.email && <p className={styles.fieldError}>{errors.email}</p>}
            </div>
          </div>

          <button type="submit" className={styles.submitBtn} disabled={is_loading}>
            {is_loading ? 'Sending…' : 'Send reset link →'}
          </button>
        </form>

        <a className={styles.backLink} onClick={() => history('/login')}>
          ← Back to log in
        </a>
      </div>
    </div>
  );
};

export default PasswordRecovery;
